import type { AgentUseCase, UseCaseContent } from "./types";

const en: UseCaseContent = {
  industry: "Lending & finance",
  title: "Credit evaluation agent",
  cardSummary:
    "Reviewers spend their day cross-checking loan documents by eye instead of deciding.",
  heroSubhead:
    "We design, build and deploy an AI agent that reads and cross-checks loan documents, flags the discrepancies, and hands your credit officer a decision-ready summary — while the lending decision stays with a human.",
  snapshot: {
    valueLabel: "High",
    valueWhy:
      "Frees roughly half a reviewer's time and applies consistent, audit-ready diligence to every file — the consistency a BNM or MAS review looks for.",
    complexityLabel: "High",
    complexityWhy:
      "Vision/OCR on scans, multi-document reconciliation, an evaluator-optimizer loop, and BNM/MAS and PDPA/PDPC-grade controls and audit trail.",
    timeToLive: "~a few weeks (one product line)",
    bestFit: "Lenders & finance teams reviewing loan document bundles",
  },
  outcomes: [
    "Reviewers spend time deciding, not matching documents",
    "The same checks, applied consistently to every file",
    "Missing months and mismatches caught every time",
    "More files cleared without adding headcount",
  ],
  impact: {
    manualLabel: "Manual review today",
    manualValue: "~25–35 min",
    agentLabel: "With the agent",
    agentValue: "~5–8 min",
    saved:
      "≈ 20–25 minutes saved per application — about 100–125 hours a month at 300 applications, roughly half a full-time reviewer.",
    note: "Indicative; actual time depends on the number of documents and case complexity.",
  },
  businessProblem:
    "A lender's credit team in the region receives each application as a bundle — IC, payslips, bank statements, financing form, dealer quotation. Most of a reviewer's day goes to the mechanical part: checking that the documents agree with each other, not deciding whether to lend. It is slow, it drifts as fatigue sets in, and two officers apply slightly different diligence to the same file.",
  manualWorkflow: {
    steps: [
      "Open the application bundle and each document",
      "Read and extract the key fields by eye",
      "Cross-check name, income and account across documents",
      "Chase missing months or mismatches",
      "Only then begin the real credit judgement",
    ],
    breaks:
      "The bottleneck is document matching, not decision-making — and it is exactly the part that fatigues and varies between officers.",
  },
  agenticWorkflow: {
    narrative:
      "We redesign the review as an evaluator-optimizer loop: the agent reads and reconciles the bundle, evaluates each check, and iterates until it can produce a clean, evidenced summary — escalating anything it cannot resolve to a human.",
    steps: [
      "Read documents (incl. scans & photos)",
      "Extract the fields that matter",
      "Cross-match across the bundle",
      "Flag discrepancies with evidence",
      "Summarise for the human to decide",
    ],
    inLabel: "An application arrives",
    outLabel: "A decision-ready summary",
    loopAround: {
      index: 2,
      label: "The evaluator re-checks and re-reads until the checks pass — the evaluator-optimizer loop.",
    },
    humanStep: "The credit officer makes the lending decision",
  },
  workedExample: {
    inIcon: "doc",
    inLabel: "In the application bundle",
    inMessage:
      "Application form: declared income RM 6,000/mo. Payslip: RM 6,000. 3 months of bank statements: salary credits averaging RM 4,150.",
    outLabel: "What the agent flags",
    outLines: [
      "Name & IC match across form, payslip and statements",
      "All 3 required months of statements present",
      "Declared income cross-checked against the actual salary credits",
    ],
    outResult:
      "1 discrepancy flagged: RM 6,000 declared vs ~RM 4,150 credited (31% gap)",
    note: "Every flag cites the exact document & figure · your officer makes the lending call · ~RM 0.60–1.20 per application.",
  },
  workflowDetail: [
    {
      title: "Read the documents",
      detail:
        "The agent ingests every file in the bundle — IC, payslips, bank statements, financing form, dealer quotation — reading text and, for scans and phone photos, using vision to extract the content.",
    },
    {
      title: "Extract structured fields",
      detail:
        "It pulls the fields that matter into a structured record — name, IC number, employer, declared income, bank account, monthly commitments — tagging the source document for each.",
    },
    {
      title: "Cross-match across the bundle",
      detail:
        "It reconciles those fields against each other: name on IC vs payslip vs application; declared income vs the actual salary credits; the statement's account vs the form's; whether all required months are present.",
    },
    {
      title: "Evaluate and iterate (the loop)",
      detail:
        "An evaluator step scores each check; where confidence is low or a check fails, the agent re-reads or requests the missing piece before finalising — the evaluator-optimizer loop.",
    },
    {
      title: "Flag and summarise",
      detail:
        "It produces a one-page summary: the applicant, the checks that passed, and the two or three items to look at — each with the figure and the document it came from.",
    },
    {
      title: "Hand to a human",
      detail:
        "The credit officer reviews the flagged items and makes the lending decision. Nothing is auto-approved.",
    },
  ],
  architecture: [
    "Pattern: an evaluator-optimizer loop on the Claude Agent SDK — each tool exposed via an in-process MCP server.",
    "Context: the whole bundle is pulled into the window — text plus vision for scans and phone photos — and long bundles are compacted so nothing is silently dropped.",
    "Tools: a document-store reader, a vision/OCR extractor, a field-matcher, and (optional) a credit-bureau or bank-statement lookup — all read-only.",
    "Guardrails: strict JSON-schema output, no write access to core systems, least-privilege, and PDPA (Malaysia) / PDPC (Singapore)-aligned handling of IC numbers, payslips and statements.",
    "Untrusted input: document text is treated as untrusted (prompt-injection resistant) — a doctored PDF can't steer the agent's decision.",
    "Recovery: where confidence is low or a document is missing, it re-reads or requests the piece, then escalates to the officer — it never guesses a decision.",
    "Output contract: it writes a structured review packet (not a chat reply) into the review queue, with the officer's decision captured against it — a machine-readable audit trail, not a screenshot.",
    "Eval harness: a golden set of historical cases (including known fraud) with deterministic checks plus an LLM-as-judge on the fuzzy calls; regression-tested on every change before it touches live applications.",
  ],
  models: [
    {
      task: "Read & extract from documents (incl. scans)",
      model: "Claude Sonnet",
      why: "Reliable vision and structured extraction on messy, real-world documents.",
    },
    {
      task: "Cross-match & reason across the bundle",
      model: "Claude Sonnet",
      why: "The workhorse — strong reasoning at moderate cost; handles the bulk of cases.",
    },
    {
      task: "Resolve ambiguous / borderline cases",
      model: "Claude Opus",
      why: "Invoked only when Sonnet flags low confidence — a small fraction of applications.",
    },
    {
      task: "High-volume simple checks",
      model: "Claude Haiku",
      why: "Cheapest and fastest for bulk yes/no checks that don't need full reasoning.",
    },
  ],
  cost: {
    rows: [
      {
        label: "AI usage — per application",
        value: "~RM 0.60–1.20",
        note: "input documents + reasoning + output summary, at Claude Sonnet token rates",
      },
      {
        label: "AI usage — at 300 applications/month",
        value: "~RM 160–310 / month",
        note: "roughly half with the Batch API for overnight, non-urgent runs",
      },
      {
        label: "Build (one-off)",
        value: "scoped per integration",
        note: "a focused single-product agent is a few weeks of engineering; we quote fixed after a short discovery",
      },
      {
        label: "Ongoing",
        value: "monitoring + support",
        note: "small next to the manual-review time it removes",
      },
    ],
    disclaimer:
      "Indicative only, shown in Ringgit at roughly RM 4.09 to the USD (mid-market, 11 Aug 2026); per-token rates follow Anthropic's published pricing (confirm current figures). Actual AI usage depends on document sizes and monthly volume. You can model the same figures in SGD or your currency with our Claude cost calculator.",
  },
  howWeBuild:
    "It's wired straight into your loan origination system and document store through connected tools — no manual exports. Guardrails and a full audit trail are built in from day one, so every flag it raises traces back to the exact document and line it came from.",
  agentLoop: [
    "the document bundle & case data",
    "extract & cross-match (MCP tools)",
    "re-check its own flags before surfacing",
  ],
  integrations: [
    "Loan origination / core system",
    "Document store or intake inbox",
    "Optional: credit-bureau or bank-statement lookups",
  ],
  modelChoice: {
    approach: "Single model",
    rationale:
      "Credit review is sequential and shares one case context, so a single well-instrumented model is the right call — reliable, cheaper, and easier to audit.",
    costNote:
      "We reserve multi-agent designs (≈15× the tokens) for genuinely parallel, breadth-first work — not for a single, tightly-coupled case file.",
  },
  evaluation: [
    "Deterministic checks first — field matches, required months present, account consistency",
    "Self-verification — the agent re-checks its own flags before surfacing them",
    "Business KPIs — reviewer time per file, consistency, throughput, and misses caught",
  ],
  evaluationDetail: {
    framework:
      "We score the agent with the Agent GPA framework — Goal, Plan, Action, a current standard for agent reliability — on top of the human baseline.",
    baseline:
      "First we measure the current manual process on a sample — time per file, how consistently two reviewers reach the same conclusion, and how many issues slip through. That human baseline is what every agent metric is measured against, so the lift is provable rather than assumed.",
    dimensions: [
      {
        dim: "Goal",
        desc: "Did it reach the right outcome?",
        metrics: [
          {
            name: "Agreement with a senior reviewer",
            measures: "Its summary, flags and recommended checks match an expert's on held-out cases",
            target: "≥ 90%",
          },
        ],
      },
      {
        dim: "Plan",
        desc: "Was the approach sound and complete?",
        metrics: [
          {
            name: "Required checks executed",
            measures: "Every mandated check is run — nothing skipped",
            target: "100%",
          },
          {
            name: "No wasteful loops",
            measures: "It re-reads only when a check genuinely fails, not needlessly",
            target: "monitored",
          },
        ],
      },
      {
        dim: "Action",
        desc: "Were the individual steps correct?",
        metrics: [
          {
            name: "Field extraction accuracy",
            measures: "Did it read each field — name, income, account — correctly?",
            target: "≥ 98%",
          },
          {
            name: "Discrepancy recall (catch rate)",
            measures: "Of the real mismatches in the golden set, how many it flags",
            target: "≥ 95%",
          },
          {
            name: "False-flag rate",
            measures: "How often it flags something that isn't actually a problem",
            target: "≤ 10%",
          },
          {
            name: "No hallucinated figures",
            measures: "Every number in the summary traces to a source document",
            target: "0 tolerated",
          },
        ],
      },
    ],
    gate: "The agent does not touch a live application until it beats the human baseline and clears these GPA targets on a golden set of historical cases — including known fraud. Every change is regression-tested against the same set before it ships.",
  },
  delivery:
    "We start with a proof-of-concept on your real historical files — including the messy and fraudulent ones — and prove reliability before it touches live applications. A focused single-product rollout is a matter of weeks.",
  ctaTitle: "Is your credit team matching documents by hand?",
  ctaDescription:
    "Tell us your product line and volumes and we'll tell you honestly whether a credit evaluation agent is worth building — and what it would take.",
  faqs: [
    {
      question: "Does the agent approve or reject loans?",
      answer:
        "No. It reads, matches and flags; a human credit officer makes the approve or decline decision. Human-in-the-loop is essential for regulated lending in the region.",
    },
    {
      question: "How is it kept compliant with BNM/MAS and PDPA/PDPC?",
      answer:
        "With a full audit trail so decisions can be reconstructed, and built around PDPA/PDPC requirements for where credit data is processed, stored and accessed. Treat this as a starting point for your compliance team, not legal advice.",
    },
    {
      question: "How long does it take to deploy?",
      answer:
        "A proof-of-concept for one focused product line is usually a matter of weeks; a full rollout takes longer, mostly because of integration and getting reviewers comfortable, not the AI.",
    },
  ],
};

const ms: UseCaseContent = {
  industry: "Pemberian pinjaman & kewangan",
  title: "Ejen penilaian kredit",
  cardSummary:
    "Penyemak menghabiskan hari menyemak silang dokumen pinjaman secara manual, bukan membuat keputusan.",
  heroSubhead:
    "Kami mereka bentuk, membina dan melaksanakan ejen AI yang membaca dan menyemak silang dokumen pinjaman, menandakan percanggahan, dan menyediakan ringkasan sedia-keputusan untuk pegawai kredit anda — sementara keputusan pinjaman kekal dengan manusia.",
  snapshot: {
    valueLabel: "Tinggi",
    valueWhy:
      "Membebaskan kira-kira separuh masa penyemak dan menerapkan usaha wajar yang konsisten dan sedia-audit pada setiap fail — kekonsistenan yang dicari oleh semakan BNM atau MAS.",
    complexityLabel: "Tinggi",
    complexityWhy:
      "Visi/OCR pada imbasan, rekonsiliasi berbilang dokumen, gelung evaluator-optimizer, dan kawalan serta jejak audit bertaraf BNM/MAS dan PDPA/PDPC.",
    timeToLive: "~beberapa minggu (satu barisan produk)",
    bestFit: "Pemberi pinjaman & pasukan kewangan yang menyemak himpunan dokumen pinjaman",
  },
  outcomes: [
    "Penyemak menghabiskan masa membuat keputusan, bukan memadan dokumen",
    "Semakan yang sama, diterapkan secara konsisten pada setiap fail",
    "Bulan yang hilang dan percanggahan ditangkap setiap kali",
    "Lebih banyak fail diselesaikan tanpa menambah kakitangan",
  ],
  impact: {
    manualLabel: "Semakan manual hari ini",
    manualValue: "~25–35 min",
    agentLabel: "Dengan ejen",
    agentValue: "~5–8 min",
    saved:
      "≈ 20–25 minit dijimatkan setiap permohonan — kira-kira 100–125 jam sebulan pada 300 permohonan, lebih kurang separuh penyemak sepenuh masa.",
    note: "Anggaran; masa sebenar bergantung pada bilangan dokumen dan kerumitan kes.",
  },
  businessProblem:
    "Pasukan kredit pemberi pinjaman di rantau ini menerima setiap permohonan sebagai satu himpunan — IC, slip gaji, penyata bank, borang pembiayaan, sebut harga pengedar. Sebahagian besar hari penyemak dihabiskan untuk bahagian mekanikal: memastikan dokumen sepadan antara satu sama lain, bukan menilai sama ada untuk memberi pinjaman. Ia perlahan, hilang ketepatan apabila letih, dan dua pegawai menerapkan usaha wajar yang berbeza pada fail yang sama.",
  manualWorkflow: {
    steps: [
      "Buka himpunan permohonan dan setiap dokumen",
      "Baca dan ekstrak medan penting secara manual",
      "Semak silang nama, pendapatan dan akaun merentas dokumen",
      "Kejar bulan yang hilang atau percanggahan",
      "Barulah bermula pertimbangan kredit sebenar",
    ],
    breaks:
      "Halangan adalah pemadanan dokumen, bukan membuat keputusan — dan itulah bahagian yang meletihkan dan berbeza antara pegawai.",
  },
  agenticWorkflow: {
    narrative:
      "Kami mereka bentuk semula semakan sebagai gelung evaluator-optimizer: ejen membaca dan menyelaraskan himpunan, menilai setiap semakan, dan mengulang sehingga ia dapat menghasilkan ringkasan yang bersih dan berbukti — mengangkat apa-apa yang tidak dapat diselesaikan kepada manusia.",
    steps: [
      "Baca dokumen (termasuk imbasan & foto)",
      "Ekstrak medan yang penting",
      "Padan silang merentas himpunan",
      "Tandakan percanggahan dengan bukti",
      "Ringkaskan untuk keputusan manusia",
    ],
    inLabel: "Permohonan tiba",
    outLabel: "Ringkasan sedia-keputusan",
    loopAround: {
      index: 2,
      label: "Penilai menyemak semula dan membaca semula sehingga semakan lulus — gelung evaluator-optimizer.",
    },
    humanStep: "Pegawai kredit membuat keputusan pinjaman",
  },
  workedExample: {
    inIcon: "doc",
    inLabel: "Dalam himpunan permohonan",
    inMessage:
      "Borang permohonan: pendapatan diisytihar RM 6,000/bln. Slip gaji: RM 6,000. Penyata bank 3 bulan: kredit gaji purata RM 4,150.",
    outLabel: "Apa yang ejen tandakan",
    outLines: [
      "Nama & IC sepadan merentas borang, slip gaji dan penyata",
      "Kesemua 3 bulan penyata yang diperlukan ada",
      "Pendapatan diisytihar disemak silang dengan kredit gaji sebenar",
    ],
    outResult:
      "1 percanggahan ditandakan: RM 6,000 diisytihar vs ~RM 4,150 dikreditkan (jurang 31%)",
    note: "Setiap tanda memetik dokumen & angka tepat · pegawai anda membuat keputusan pinjaman · ~RM 0.60–1.20 setiap permohonan.",
  },
  workflowDetail: [
    {
      title: "Baca dokumen",
      detail:
        "Ejen membaca setiap fail dalam himpunan — IC, slip gaji, penyata bank, borang pembiayaan, sebut harga pengedar — membaca teks dan, untuk imbasan serta foto telefon, menggunakan visi untuk mengekstrak kandungan.",
    },
    {
      title: "Ekstrak medan berstruktur",
      detail:
        "Ia menarik medan penting ke dalam rekod berstruktur — nama, nombor IC, majikan, pendapatan diisytihar, akaun bank, komitmen bulanan — menandakan dokumen sumber bagi setiap satu.",
    },
    {
      title: "Padan silang merentas himpunan",
      detail:
        "Ia menyelaraskan medan tersebut antara satu sama lain: nama pada IC vs slip gaji vs permohonan; pendapatan diisytihar vs kredit gaji sebenar; akaun penyata vs borang; sama ada semua bulan diperlukan hadir.",
    },
    {
      title: "Nilai dan ulang (gelung)",
      detail:
        "Langkah penilai menskor setiap semakan; jika keyakinan rendah atau semakan gagal, ejen membaca semula atau meminta bahagian yang hilang sebelum memuktamadkan — gelung evaluator-optimizer.",
    },
    {
      title: "Tanda dan ringkaskan",
      detail:
        "Ia menghasilkan ringkasan satu halaman: pemohon, semakan yang lulus, dan dua atau tiga perkara untuk dilihat — setiap satu dengan angka dan dokumen sumbernya.",
    },
    {
      title: "Serah kepada manusia",
      detail:
        "Pegawai kredit menyemak perkara yang ditandakan dan membuat keputusan pinjaman. Tiada apa-apa diluluskan secara automatik.",
    },
  ],
  architecture: [
    "Corak: gelung evaluator-optimizer atas Claude Agent SDK — setiap alat didedahkan melalui pelayan MCP dalam-proses.",
    "Konteks: seluruh himpunan ditarik ke tetingkap — teks serta visi untuk imbasan dan foto telefon — dan himpunan panjang dimampatkan supaya tiada yang tercicir secara senyap.",
    "Alat: pembaca simpanan dokumen, pengekstrak visi/OCR, pemadan medan, dan (pilihan) semakan biro kredit atau penyata bank — semuanya baca-sahaja.",
    "Pagar keselamatan: output skema-JSON yang ketat, tiada akses tulis ke sistem teras, keistimewaan-minimum, dan pengendalian selaras PDPA (Malaysia) / PDPC (Singapura) untuk nombor IC, slip gaji dan penyata.",
    "Input tidak dipercayai: teks dokumen dianggap tidak dipercayai (tahan suntikan-prompt) — PDF yang diusik tidak boleh mengemudi keputusan ejen.",
    "Pemulihan: apabila keyakinan rendah atau dokumen hilang, ia membaca semula atau meminta bahagian itu, kemudian menyerahkan kepada pegawai — ia tidak pernah meneka keputusan.",
    "Kontrak output: ia menulis pakej semakan berstruktur (bukan balasan chat) ke baris gilir semakan, dengan keputusan pegawai direkod terhadapnya — jejak audit boleh-dibaca-mesin, bukan tangkap skrin.",
    "Rangka penilaian: set emas kes sejarah (termasuk penipuan diketahui) dengan semakan deterministik serta LLM-sebagai-hakim untuk panggilan kabur; diuji regresi pada setiap perubahan sebelum menyentuh permohonan langsung.",
  ],
  models: [
    {
      task: "Baca & ekstrak daripada dokumen (termasuk imbasan)",
      model: "Claude Sonnet",
      why: "Visi yang boleh dipercayai dan ekstrak berstruktur pada dokumen dunia sebenar yang berselerak.",
    },
    {
      task: "Padan silang & menaakul merentas himpunan",
      model: "Claude Sonnet",
      why: "Kuda kerja — penaakulan kuat pada kos sederhana; mengendalikan sebahagian besar kes.",
    },
    {
      task: "Selesaikan kes samar / sempadan",
      model: "Claude Opus",
      why: "Digunakan hanya apabila Sonnet menandakan keyakinan rendah — sebahagian kecil permohonan.",
    },
    {
      task: "Semakan mudah bervolum tinggi",
      model: "Claude Haiku",
      why: "Paling murah dan pantas untuk semakan ya/tidak pukal yang tidak memerlukan penaakulan penuh.",
    },
  ],
  cost: {
    rows: [
      {
        label: "Penggunaan AI — setiap permohonan",
        value: "~RM 0.60–1.20",
        note: "dokumen input + penaakulan + ringkasan output, pada kadar token Claude Sonnet",
      },
      {
        label: "Penggunaan AI — pada 300 permohonan/bulan",
        value: "~RM 160–310 / bulan",
        note: "lebih kurang separuh dengan Batch API untuk larian bukan segera pada waktu malam",
      },
      {
        label: "Pembinaan (sekali)",
        value: "diskop mengikut integrasi",
        note: "ejen satu produk yang fokus adalah beberapa minggu kejuruteraan; kami beri sebut harga tetap selepas penemuan ringkas",
      },
      {
        label: "Berterusan",
        value: "pemantauan + sokongan",
        note: "kecil berbanding masa semakan manual yang dihapuskan",
      },
    ],
    disclaimer:
      "Anggaran sahaja, ditunjukkan dalam Ringgit pada kira-kira RM 4.09 kepada USD (kadar pasaran, 11 Ogos 2026); kadar setiap token mengikut harga yang diterbitkan Anthropic (sahkan angka semasa). Penggunaan AI sebenar bergantung pada saiz dokumen dan volum bulanan. Anda boleh anggarkan angka yang sama dalam SGD atau mata wang anda dengan kalkulator kos Claude kami.",
  },
  howWeBuild:
    "Ia disambung terus ke sistem asal pinjaman dan simpanan dokumen anda melalui alat yang disambung — tanpa eksport manual. Pagar keselamatan dan jejak audit penuh dibina dari hari pertama, jadi setiap tanda yang dibangkitkan dijejak kembali ke dokumen dan baris tepat asalnya.",
  agentLoop: [
    "himpunan dokumen & data kes",
    "ekstrak & padan silang (alat MCP)",
    "semak semula tandanya sendiri sebelum memaparkan",
  ],
  integrations: [
    "Sistem asal pinjaman / sistem teras",
    "Simpanan dokumen atau peti masuk",
    "Pilihan: semakan biro kredit atau penyata bank",
  ],
  modelChoice: {
    approach: "Model tunggal",
    rationale:
      "Semakan kredit adalah berurutan dan berkongsi satu konteks kes, jadi model tunggal yang lengkap adalah pilihan tepat — boleh dipercayai, lebih murah dan lebih mudah diaudit.",
    costNote:
      "Kami menyimpan reka bentuk berbilang-ejen (≈15× token) untuk kerja yang benar-benar selari — bukan untuk satu fail kes yang berkait rapat.",
  },
  evaluation: [
    "Semakan deterministik dahulu — padanan medan, bulan diperlukan hadir, ketekalan akaun",
    "Pengesahan sendiri — ejen menyemak semula tandanya sebelum memaparkannya",
    "KPI perniagaan — masa penyemak setiap fail, ketekalan, daya pemprosesan, dan yang tertangkap",
  ],
  evaluationDetail: {
    framework:
      "Kami menilai ejen dengan rangka kerja Agent GPA — Goal, Plan, Action, satu standard semasa untuk kebolehpercayaan ejen — di atas garis dasar manusia.",
    baseline:
      "Mula-mula kami mengukur proses manual semasa pada sampel — masa setiap fail, sejauh mana konsisten dua penyemak mencapai keputusan sama, dan berapa banyak isu terlepas. Garis dasar manusia itu menjadi ukuran untuk setiap metrik ejen, jadi peningkatan boleh dibuktikan, bukan diandaikan.",
    dimensions: [
      {
        dim: "Goal",
        desc: "Adakah ia mencapai hasil yang betul?",
        metrics: [
          {
            name: "Persetujuan dengan penyemak kanan",
            measures: "Ringkasan, tanda dan semakan yang disyorkan sepadan dengan pakar pada kes terpencil",
            target: "≥ 90%",
          },
        ],
      },
      {
        dim: "Plan",
        desc: "Adakah pendekatannya kukuh dan lengkap?",
        metrics: [
          {
            name: "Semakan diperlukan dilaksanakan",
            measures: "Setiap semakan wajib dijalankan — tiada yang dilangkau",
            target: "100%",
          },
          {
            name: "Tiada gelung membazir",
            measures: "Ia membaca semula hanya apabila semakan benar-benar gagal, bukan sia-sia",
            target: "dipantau",
          },
        ],
      },
      {
        dim: "Action",
        desc: "Adakah setiap langkah betul?",
        metrics: [
          {
            name: "Ketepatan ekstrak medan",
            measures: "Adakah ia membaca setiap medan — nama, pendapatan, akaun — dengan betul?",
            target: "≥ 98%",
          },
          {
            name: "Recall percanggahan (kadar tangkap)",
            measures: "Daripada percanggahan sebenar dalam set emas, berapa yang ditandakan",
            target: "≥ 95%",
          },
          {
            name: "Kadar tanda palsu",
            measures: "Berapa kerap ia menandakan sesuatu yang bukan masalah sebenar",
            target: "≤ 10%",
          },
          {
            name: "Tiada angka halusinasi",
            measures: "Setiap nombor dalam ringkasan boleh dijejak ke dokumen sumber",
            target: "0 dibenarkan",
          },
        ],
      },
    ],
    gate: "Ejen tidak menyentuh permohonan langsung sehingga ia mengatasi garis dasar manusia dan mencapai sasaran GPA ini pada set emas kes sejarah — termasuk penipuan diketahui. Setiap perubahan diuji regresi terhadap set yang sama sebelum dihantar.",
  },
  delivery:
    "Kami bermula dengan bukti-konsep atas fail sejarah sebenar anda — termasuk yang berselerak dan penipuan — dan membuktikan kebolehpercayaan sebelum ia menyentuh permohonan langsung. Pelancaran satu produk yang fokus mengambil masa beberapa minggu.",
  ctaTitle: "Adakah pasukan kredit anda memadan dokumen secara manual?",
  ctaDescription:
    "Beritahu kami barisan produk dan volum anda, dan kami akan beritahu dengan jujur sama ada ejen penilaian kredit berbaloi dibina — dan apa yang diperlukan.",
  faqs: [
    {
      question: "Adakah ejen meluluskan atau menolak pinjaman?",
      answer:
        "Tidak. Ia membaca, memadan dan menandakan; pegawai kredit manusia membuat keputusan lulus atau tolak. Manusia-dalam-gelung penting untuk pemberian pinjaman terkawal di rantau ini.",
    },
    {
      question: "Bagaimana ia mematuhi BNM/MAS dan PDPA/PDPC?",
      answer:
        "Dengan jejak audit penuh supaya keputusan boleh dibina semula, dan dibina mengikut keperluan PDPA/PDPC tentang di mana data kredit diproses, disimpan dan diakses. Anggap ini sebagai titik permulaan untuk pasukan pematuhan anda, bukan nasihat undang-undang.",
    },
    {
      question: "Berapa lama untuk dilaksanakan?",
      answer:
        "Bukti-konsep untuk satu barisan produk yang fokus biasanya beberapa minggu; pelancaran penuh mengambil masa lebih lama, kebanyakannya kerana integrasi dan membiasakan penyemak, bukan AI.",
    },
  ],
};

const zh: UseCaseContent = {
  industry: "借贷与金融",
  title: "信贷评估智能体",
  cardSummary: "审核人员整天用肉眼交叉核对贷款文件，而不是做决策。",
  heroSubhead:
    "我们设计、构建并部署一个 AI 智能体，读取并交叉核对贷款文件、标记差异，并为你的信贷主管提供可直接决策的摘要——而放贷决定仍由人来做。",
  snapshot: {
    valueLabel: "高",
    valueWhy:
      "释放约半名审核人员的时间，并对每一份文件施加一致、可审计的尽职审查——正是 BNM 或 MAS 审查所看重的一致性。",
    complexityLabel: "高",
    complexityWhy:
      "对扫描件的视觉/OCR、多文档交叉核对、evaluator-optimizer 循环，以及 BNM/MAS 与 PDPA/PDPC 级的管控与审计轨迹。",
    timeToLive: "约数周（单一产品线）",
    bestFit: "审阅贷款文件材料的放贷机构与财务团队",
  },
  outcomes: [
    "审核人员把时间用在决策上，而不是比对文件",
    "同样的检查，一致地应用到每一份文件",
    "缺失的月份和不一致之处每次都能被发现",
    "在不增加人手的情况下处理更多文件",
  ],
  impact: {
    manualLabel: "当前人工审核",
    manualValue: "约 25–35 分钟",
    agentLabel: "使用智能体",
    agentValue: "约 5–8 分钟",
    saved:
      "每份申请约节省 20–25 分钟——每月 300 份申请约合 100–125 小时，大约相当于半名全职审核人员。",
    note: "为估算；实际时间取决于文件数量与案件复杂度。",
  },
  businessProblem:
    "区域内的放贷机构的信贷团队将每份申请作为一整套材料接收——身份证、薪资单、银行对账单、融资表格、经销商报价。审核人员一天的大部分时间花在机械性工作上：核对各文件是否一致，而不是判断是否放贷。这个过程缓慢，疲劳时容易出错，两名主管对同一份文件的尽职程度也略有不同。",
  manualWorkflow: {
    steps: [
      "打开申请材料及每份文件",
      "用肉眼阅读并提取关键字段",
      "跨文件交叉核对姓名、收入与账户",
      "追查缺失的月份或不一致之处",
      "之后才开始真正的信贷判断",
    ],
    breaks: "瓶颈在于文件比对，而非决策——而这恰恰是最容易疲劳、且因人而异的部分。",
  },
  agenticWorkflow: {
    narrative:
      "我们将审核重新设计为 evaluator-optimizer 循环：智能体读取并核对整套材料，评估每一项检查，并反复迭代，直到能生成一份干净、有证据支撑的摘要——无法解决的则上报给人处理。",
    steps: [
      "读取文件（含扫描件与照片）",
      "提取关键字段",
      "跨材料交叉比对",
      "带证据标记差异",
      "生成摘要供人决策",
    ],
    inLabel: "一份申请进入",
    outLabel: "一份可直接决策的摘要",
    loopAround: {
      index: 2,
      label: "评估器会反复核对并重新读取，直到检查通过——即 evaluator-optimizer 循环。",
    },
    humanStep: "信贷主管做出放贷决定",
  },
  workedExample: {
    inIcon: "doc",
    inLabel: "在申请材料中",
    inMessage:
      "申请表：申报月收入 RM 6,000。薪资单：RM 6,000。3 个月银行对账单：工资入账平均 RM 4,150。",
    outLabel: "智能体标记出什么",
    outLines: [
      "姓名与身份证号在表格、薪资单与对账单间一致",
      "所需的 3 个月对账单齐全",
      "申报收入与实际工资入账已交叉核对",
    ],
    outResult: "标记 1 处差异：申报 RM 6,000 vs 实际入账约 RM 4,150（差 31%）",
    note: "每个标记都引用确切的文件与数字 · 由你的主管做放贷决定 · 每份申请约 RM 0.60–1.20。",
  },
  workflowDetail: [
    {
      title: "读取文件",
      detail:
        "智能体读取整套材料中的每份文件——身份证、薪资单、银行对账单、融资表格、经销商报价——读取文字，并对扫描件和手机照片使用视觉能力提取内容。",
    },
    {
      title: "提取结构化字段",
      detail:
        "它将关键字段提取为一条结构化记录——姓名、身份证号、雇主、申报收入、银行账户、每月负债——并为每一项标注来源文件。",
    },
    {
      title: "跨材料交叉比对",
      detail:
        "它将这些字段相互核对：身份证 vs 薪资单 vs 申请上的姓名；申报收入 vs 实际工资入账；对账单账户 vs 表格账户；所需月份是否齐全。",
    },
    {
      title: "评估并迭代（循环）",
      detail:
        "评估步骤为每项检查打分；当置信度低或检查未通过时，智能体会重新读取或索取缺失的部分再定稿——即 evaluator-optimizer 循环。",
    },
    {
      title: "标记并生成摘要",
      detail:
        "它生成一页式摘要：申请人、通过的检查、以及需要关注的两三项——每一项都附带具体数字及其来源文件。",
    },
    {
      title: "交给人处理",
      detail:
        "信贷主管审阅被标记的项目并做出放贷决定。没有任何内容会被自动批准。",
    },
  ],
  architecture: [
    "模式：基于 Claude Agent SDK 的 evaluator-optimizer 循环——每个工具通过进程内 MCP 服务器提供。",
    "上下文：整份材料被载入窗口——文本加上对扫描件与手机照片的视觉识别——较长的材料会被压缩，确保没有内容被静默丢弃。",
    "工具：文档库读取器、视觉/OCR 提取器、字段匹配器，以及（可选）征信局或银行对账单查询——全部只读。",
    "防护机制：严格的 JSON 模式输出、对核心系统无写入权限、最小权限、以及对身份证号、薪资单和对账单符合 PDPA（马来西亚）/ PDPC（新加坡）的处理。",
    "不可信输入：文档文本被视为不可信（可抵御提示注入）——被篡改的 PDF 无法左右智能体的判断。",
    "恢复：当置信度低或缺少文档时，它会重新读取或索取该件，然后上交给主管——它绝不臆测决定。",
    "输出契约：它把一份结构化的审阅包（而非聊天回复）写入审阅队列，主管的决定对应记录于其上——一条机器可读的审计轨迹，而非截图。",
    "评估框架：一组历史案例的黄金集（含已知欺诈）配合确定性检查，并对模糊判断采用 LLM 作为评审；每次改动在接触实时申请前都经过回归测试。",
  ],
  models: [
    {
      task: "读取并提取文件内容（含扫描件）",
      model: "Claude Sonnet",
      why: "对杂乱的真实文件具备可靠的视觉与结构化提取能力。",
    },
    {
      task: "跨材料交叉比对与推理",
      model: "Claude Sonnet",
      why: "主力模型——中等成本下的强推理能力，处理绝大多数案件。",
    },
    {
      task: "处理模糊 / 临界案件",
      model: "Claude Opus",
      why: "仅在 Sonnet 标记低置信度时调用——占申请的一小部分。",
    },
    {
      task: "高频简单检查",
      model: "Claude Haiku",
      why: "对无需完整推理的批量是/否检查最便宜、最快。",
    },
  ],
  cost: {
    rows: [
      {
        label: "AI 用量——每份申请",
        value: "约 RM 0.60–1.20",
        note: "输入文件 + 推理 + 输出摘要，按 Claude Sonnet 的 token 费率",
      },
      {
        label: "AI 用量——每月 300 份申请",
        value: "约 RM 160–310 / 月",
        note: "使用 Batch API 处理非紧急的夜间任务约可减半",
      },
      {
        label: "构建（一次性）",
        value: "按集成范围报价",
        note: "聚焦单一产品线的智能体约需数周工程；简短的需求梳理后我们给出固定报价",
      },
      {
        label: "持续运营",
        value: "监控 + 支持",
        note: "相比其省去的人工审核时间，成本很小",
      },
    ],
    disclaimer:
      "仅为估算，按约 1 美元兑 4.09 令吉（市场中间价，2026 年 8 月 11 日）以令吉显示；每 token 费率以 Anthropic 公布的定价为准（请核对最新数字）。实际 AI 用量取决于文件大小与每月业务量。你可以用我们的 Claude 成本计算器把同样的数字换算成 SGD 或你的货币。",
  },
  howWeBuild:
    "它通过连接的工具直接接入你的贷款发起系统与文档库——无需手动导出。防护机制与完整审计轨迹从第一天就内建，因此它提出的每一个标记都能追溯到其来源的确切文件与行。",
  agentLoop: ["文件材料与案件数据", "提取并交叉比对（MCP 工具）", "在呈现前自查其标记"],
  integrations: ["贷款发起 / 核心系统", "文件库或收件箱", "可选：征信局或银行对账单查询"],
  modelChoice: {
    approach: "单一模型",
    rationale:
      "信贷审核是顺序进行、共享同一案件上下文的，因此一个完善配置的单一模型是正确选择——可靠、更省成本、也更易审计。",
    costNote:
      "我们把多智能体设计（约 15 倍 token）留给真正并行、广度优先的工作——而非一个紧耦合的单一案件。",
  },
  evaluation: [
    "先做确定性检查——字段匹配、必需月份齐全、账户一致性",
    "自我验证——智能体在呈现前自查其标记",
    "业务 KPI——每份文件的审核时间、一致性、处理量、以及拦截到的问题",
  ],
  evaluationDetail: {
    framework:
      "我们采用 Agent GPA 框架——Goal、Plan、Action，当前衡量智能体可靠性的标准——并结合人工基线来评估。",
    baseline:
      "我们先在样本上测量当前的人工流程——每份文件耗时、两名审核员得出相同结论的一致程度、以及有多少问题漏掉。这个人工基线是衡量每个智能体指标的基准，因此提升是可证明的，而非假设。",
    dimensions: [
      {
        dim: "Goal",
        desc: "是否达成了正确的结果？",
        metrics: [
          {
            name: "与资深审核员的一致度",
            measures: "在留出案例上，其摘要、标记与建议检查是否与专家一致",
            target: "≥ 90%",
          },
        ],
      },
      {
        dim: "Plan",
        desc: "方法是否合理且完整？",
        metrics: [
          {
            name: "必需检查全部执行",
            measures: "每一项规定检查都执行——不遗漏",
            target: "100%",
          },
          {
            name: "无无谓的循环",
            measures: "仅在检查确实未通过时才重新读取，而非无端反复",
            target: "持续监控",
          },
        ],
      },
      {
        dim: "Action",
        desc: "各个步骤是否正确？",
        metrics: [
          {
            name: "字段提取准确率",
            measures: "是否正确读取每个字段——姓名、收入、账户？",
            target: "≥ 98%",
          },
          {
            name: "差异召回率（拦截率）",
            measures: "黄金集中真实的不一致，有多少被标记出来",
            target: "≥ 95%",
          },
          {
            name: "误报率",
            measures: "多久会标记出并非真正问题的内容",
            target: "≤ 10%",
          },
          {
            name: "无凭空捏造的数字",
            measures: "摘要中的每个数字都能追溯到来源文件",
            target: "零容忍",
          },
        ],
      },
    ],
    gate: "在智能体于历史案例黄金集（含已知欺诈）上超过人工基线并达到这些 GPA 目标之前，它不会接触任何实时申请。每次改动在上线前都要针对同一数据集做回归测试。",
  },
  delivery:
    "我们先在你真实的历史文件上做概念验证——包括杂乱和欺诈的案例——在接触实时申请前先证明可靠性。聚焦单一产品线的落地只需数周。",
  ctaTitle: "你的信贷团队还在用人工比对文件吗？",
  ctaDescription:
    "告诉我们你的产品线和业务量，我们会诚实告诉你信贷评估智能体是否值得构建，以及需要什么。",
  faqs: [
    {
      question: "智能体会批准或拒绝贷款吗？",
      answer:
        "不会。它负责读取、比对和标记；由人类信贷主管做出批准或拒绝的决定。在区域内的受监管借贷中，人在环路中至关重要。",
    },
    {
      question: "如何符合 BNM/MAS 与 PDPA/PDPC 的要求？",
      answer:
        "通过完整的审计轨迹让决定可被还原，并围绕 PDPA/PDPC 对信贷数据处理、存储与访问位置的要求来构建。请将此视为你合规团队的起点，而非法律意见。",
    },
    {
      question: "部署需要多长时间？",
      answer:
        "针对单一聚焦产品线的概念验证通常只需数周；全面落地需要更长时间，主要是因为集成和让审核人员适应，而不是 AI 本身。",
    },
  ],
};

export const creditEvaluation: AgentUseCase = {
  structure: {
    slug: "credit-evaluation",
    status: "available",
    pattern: "evaluator-optimizer",
    route: "solutions/agent-use-cases/credit-evaluation",
    heroImage: "/images/agent-use-cases/credit-evaluation-hero.jpg",
    cardImage: "/images/agent-use-cases/credit-evaluation-hero.jpg",
    relatedBlogSlug: "credit-evaluation-agent-malaysia",
    relatedUseCases: ["insurance-claims", "invoice-matching"],
    trackLocation: "agent_use_case_credit_evaluation",
    valueScore: 4,
    complexityScore: 4,
  },
  content: { en, ms, zh },
};
